import { useEffect, useRef } from "react";
import useStores from "../../hooks/use-stores";
import { GetUserProfile, IsAuthed } from "../../utilities/general";
import { IsZero } from "../../utilities/field-validation";

let _permissionInFlight = false;
let _permissionLastRun = 0;
const PERMISSION_THROTTLE_MS = 30_000;

export const PermissionProvider = ({ children }: { children: any }) => {
  const { packageStore, clinicStore, userStore } = useStores();

  const syncPermission = async () => {
    if (!IsAuthed()) return;
    if (document.visibilityState !== "visible") return;
    // /iframe-auth (the platform's remote-login handshake — see
    // src/pages/iframe-auth.tsx) writes the raw impersonation token to
    // localStorage and THEN navigates to /select-clinic, which is what
    // actually calls selectClinic and populates real permissions. The instant
    // that token exists, IsAuthed() above turns true.
    //
    // /select-clinic itself is included in this skip too, and that part isn't
    // iframe-specific: this poll's own focus/visibilitychange triggers can
    // fire the instant the user interacts with the page (e.g. clicking
    // "Start Working" — or, for the iframe case, just the iframe gaining
    // focus at all — dispatches a `focus` event on this window), which can
    // land BEFORE selectClinic's own token exchange finishes and calls
    // getMemberPackagePermission() with the pre-exchange token. The backend
    // only accepts that token for login + Select/Clinic, not arbitrary
    // member-level endpoints, so it 401s — and since that sets the
    // module-level authExpired flag (network.ts), it then poisons every
    // OTHER in-flight request too, including the legitimate one
    // /select-clinic itself was about to make with the real token. Skip
    // entirely while on either page; /select-clinic drives its own
    // permission fetch, in the right order, once the token is actually ready.
    if (window.location.pathname.startsWith("/iframe-auth") || window.location.pathname.startsWith("/select-clinic")) return;
    // clinic/info.tsx / clinic/products/index.tsx scope permissions to a
    // clinic that isn't the user's real currentClinicId via a temp token
    // (UserStore.isTempPermissionActive) — getMyPermissions has no way to ask
    // for "the real clinic's permissions" specifically once that token is
    // active, and it unconditionally writes whatever it gets back into the
    // real permissionCodes. Skipping this poll entirely while temp mode is on
    // prevents it from silently overwriting the user's real session
    // permissions with the temp clinic's.
    if (userStore.isTempPermissionActive) return;

    const now = Date.now();
    if (now - _permissionLastRun < PERMISSION_THROTTLE_MS) return;
    if (_permissionInFlight) return;

    try {
      _permissionInFlight = true;
      // getMyPermissions (RBAC) infers "which clinic" purely from the
      // token's clinic scope (set by selectClinic) — a member with no
      // clinic at all, or one that hasn't selected a currentClinicId yet
      // (e.g. skipped clinic creation during onboarding), has no clinic to
      // fetch that FOR, so skip it. getMemberPackagePermission is member-
      // level (keyed by memberId, not clinic), so it still runs regardless —
      // same poll, same throttle, independent failures either way.
      const userInfo = GetUserProfile();
      const hasSelectedClinic = Boolean(userInfo?.availableClinics?.length) && !IsZero(userInfo?.currentClinicId);
      const tasks: Promise<any>[] = [packageStore.getMemberPackagePermission()];
      if (hasSelectedClinic) tasks.push(clinicStore.getMyPermissions());
      await Promise.allSettled(tasks);
      _permissionLastRun = now;
    } finally {
      _permissionInFlight = false;
    }
  };

  useEffect(() => {
    syncPermission();

    const onVisibility = () => syncPermission();
    const onFocus = () => syncPermission();

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    // Skip the periodic interval on pages where re-rendering would disrupt the
    // user's work (e.g. unsaved template edits). Focus/visibility triggers
    // still run so permissions sync when the user actively returns to the tab.
    const INTERVAL_SKIP_PATHS = ["/profile/select-template"];
    const interval = window.setInterval(() => {
      if (INTERVAL_SKIP_PATHS.some((p) => window.location.pathname.startsWith(p))) return;
      syncPermission();
    }, 2 * 60 * 1000);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
      window.clearInterval(interval);
    };
  }, []);

  return children;
};